import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import {
  Building2,
  Briefcase,
  Loader2,
  MapPin,
  Phone,
  Plus,
  Save,
  ShieldCheck,
  Tag,
  User,
  UserRound,
  Users,
  X,
  Pencil,
  Trash2,
  FileText,
  Sparkles,
} from "lucide-react";

import { getAllBranches, getBranchById } from "../../../api/hrm/branches/info";
import { getAllCustomerTags } from "../../../api/crm/customers/customerTagInfo";
import { getAllEmployeesNoPageHasFilter } from "../../../api/hrm/employees/employeeInfo";
import { createCustomer, updateCustomer, getSimpleCustomers } from "../../../api/crm/customers/customerInfo";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "../../../components/ConfirmDialog";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import ContactRepresentativeModal from "./representatives/ContactRepresentativeModal";
import { startCustomerCreateModalTour } from "./customerCreateModalTour";

const leadSourceLabels = {
  Facebook: "Facebook",
  Website: "Website",
  TikTok: "TikTok",
  Referral: "Giới thiệu",
  Event: "Workshop / Sự kiện",
};

const leadSourceOptions = [
  { value: "Facebook", label: "Facebook" },
  { value: "Website", label: "Website" },
  { value: "TikTok", label: "TikTok" },
  { value: "Referral", label: "Giới thiệu" },
  { value: "Event", label: "Workshop / Sự kiện" },
];

const customerTypeOptions = [
  { value: "Trường học", label: "Trường học" },
  { value: "Trung tâm", label: "Trung tâm" },
  { value: "Khác", label: "Khác" },
];

const schoolLevelOptions = [
  { value: "Mầm non", label: "Mầm non" },
  { value: "Tiểu học", label: "Tiểu học" },
  { value: "THCS", label: "THCS" },
  { value: "THPT", label: "THPT" },
];

const customerStatusOptions = [
  { value: "new", label: "Mới" },
  { value: "active", label: "Đang chăm sóc" },
  { value: "idle", label: "Không tương tác" },
  { value: "lost", label: "Mất khách" },
];

/** Vòng đời hồ sơ (sáp nhập/đóng) — tách biệt trạng thái chăm sóc CRM */
const recordStatusOptions = [
  { value: "active", label: "Đang hoạt động" },
  { value: "closed", label: "Đã đóng" },
  { value: "merged", label: "Đã sáp nhập" },
];

const BRANCH_PICKER_ADMIN_EMAIL = "administrator@hamesoft.com";

function readSessionUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

function canPickCustomerBranch(user = readSessionUser()) {
  return (
    String(user?.email || "").trim().toLowerCase() === BRANCH_PICKER_ADMIN_EMAIL
  );
}

function readUserBranchId(user = readSessionUser()) {
  return user?.branch?.id || user?.branch_id || null;
}

const Section = ({ icon: Icon, title, subTitle, children }) => (
  <div className="space-y-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
    <div className="flex items-center gap-3 border-b border-gray-50 pb-2">
      <div className="rounded-lg bg-red-50 p-2">
        <Icon className="h-5 w-5 text-red-600" />
      </div>
      <div>
        <h3 className="text-sm font-bold uppercase tracking-tight text-gray-800">{title}</h3>
        {subTitle ? <p className="text-xs text-gray-400">{subTitle}</p> : null}
      </div>
    </div>
    <div className="pt-2">{children}</div>
  </div>
);

const emptyForm = {
  name: "",
  phone: "",
  address: "",
  ward: "",
  student_count: "",
  branch_id: "",
  owner_id: "",
  lead_source: "",
  customer_type: "",
  school_level: "",
  customer_status: "new",
  record_status: "active",
  successor_customer_id: "",
  closed_note: "",
  tags: [],
  note: "",
};

export default function CustomerCreateModal({
  isOpen,
  onClose,
  onSuccess,
  editingCustomer,
}) {
  const [loading, setLoading] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [salesOptions, setSalesOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [successorOptions, setSuccessorOptions] = useState([]);
  const [representatives, setRepresentatives] = useState([]);
  const [repModalOpen, setRepModalOpen] = useState(false);
  const [editingRepIndex, setEditingRepIndex] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [canPickBranch, setCanPickBranch] = useState(false);

  const requiresLeadSource = !editingCustomer;
  const handleQuickTour = useCallback(() => {
    window.setTimeout(
      () => startCustomerCreateModalTour({ isEdit: !!editingCustomer }),
      80
    );
  }, [editingCustomer]);
  const isInvalid =
    !formData.name?.trim() ||
    !formData.branch_id ||
    !formData.customer_type ||
    (requiresLeadSource && !formData.lead_source) ||
    (formData.customer_type === "Trường học" && !formData.school_level) ||
    (formData.record_status === "merged" && !formData.successor_customer_id);

  const selectStyles = useMemo(
    () => ({
      ...customSelectStyles,
      control: (provided, state) => ({
        ...(typeof customSelectStyles.control === "function"
          ? customSelectStyles.control(provided, state)
          : provided),
        minHeight: 44,
        borderRadius: 12,
        borderColor: state.isFocused ? "#f87171" : "#e5e7eb",
        boxShadow: state.isFocused ? "0 0 0 4px rgba(248, 113, 113, 0.08)" : "none",
      }),
      menuPortal: (base) => ({ ...base, zIndex: 999999 }),
    }),
    []
  );

  const resetForm = () => {
    setFormData(emptyForm);
    setRepresentatives([]);
    setEditingRepIndex(null);
  };

  useEffect(() => {
    if (!isOpen) return;

    const user = readSessionUser();
    const pickBranch = canPickCustomerBranch(user);
    const sessionBranchId = readUserBranchId(user);
    setCanPickBranch(pickBranch);

    const loadLockedBranchOptions = async (branchId) => {
      if (!branchId) {
        setBranchOptions([]);
        return;
      }
      const labelFromSession = user?.branch?.name;
      if (labelFromSession) {
        setBranchOptions([{ value: branchId, label: labelFromSession }]);
        return;
      }
      try {
        const br = await getBranchById(branchId);
        setBranchOptions([{ value: branchId, label: br?.name || "Chi nhánh" }]);
      } catch {
        setBranchOptions([{ value: branchId, label: "Chi nhánh" }]);
      }
    };

    const employeesBranchId =
      editingCustomer?.branch_id || (pickBranch ? undefined : sessionBranchId) || undefined;

    Promise.all([
      pickBranch ? getAllBranches() : Promise.resolve(null),
      getAllCustomerTags(),
      getAllEmployeesNoPageHasFilter({
        status: "active",
        branch_id: employeesBranchId,
      }),
    ])
      .then(async ([branches, tags, employees]) => {
        if (pickBranch) {
          setBranchOptions(
            (branches?.items || []).map((b) => ({
              value: b.id,
              label: b.name,
            }))
          );
        } else {
          const lockedId = editingCustomer?.branch_id || sessionBranchId;
          await loadLockedBranchOptions(lockedId);
          if (!editingCustomer && sessionBranchId) {
            setFormData((prev) => ({ ...prev, branch_id: sessionBranchId }));
          }
        }

        setTagOptions(
          (tags?.items || []).map((t) => ({
            label: t.name,
            value: t.id,
            color: t.color,
          }))
        );

        setSalesOptions(
          (employees?.items || []).map((u) => ({
            value: u.id,
            label: u.employee_name,
          }))
        );
      })
      .catch((err) => {
        console.error("❌ Error loading initial data:", err);
        // Không chặn mở form khi thiếu quyền phụ (tags / danh sách NV)
        setTagOptions((prev) => prev);
        setSalesOptions((prev) => prev);
      });
  }, [isOpen, editingCustomer]);

  useEffect(() => {
    if (!isOpen) return;
    if (!formData.branch_id) {
      setSalesOptions([]);
      setFormData((prev) => ({ ...prev, owner_id: "" }));
      return;
    }

    getAllEmployeesNoPageHasFilter({
      status: "active",
      branch_id: formData.branch_id,
    })
      .then((employees) => {
        const options = (employees?.items || []).map((u) => ({
          value: u.id,
          label: u.employee_name,
        }));
        setSalesOptions(options);
        setFormData((prev) => {
          if (!prev.owner_id) return prev;
          const exists = options.some((opt) => opt.value === prev.owner_id);
          return exists ? prev : { ...prev, owner_id: "" };
        });
      })
      .catch(() => setSalesOptions([]));
  }, [isOpen, formData.branch_id]);

  useEffect(() => {
    if (!isOpen) return;

    if (!editingCustomer) {
      resetForm();
      return;
    }

    setFormData({
      name: editingCustomer.name || "",
      phone: editingCustomer.phone || "",
      address: editingCustomer.address || "",
      ward: editingCustomer.ward || "",
      student_count:
        editingCustomer.student_count != null && editingCustomer.student_count !== ""
          ? String(editingCustomer.student_count)
          : "",
      branch_id: editingCustomer.branch_id || "",
      owner_id: editingCustomer.owner_id || "",
      lead_source: editingCustomer.lead_source || "",
      customer_type: editingCustomer.customer_type || "",
      school_level: editingCustomer.school_level || "",
      customer_status: editingCustomer.status || "new",
      record_status: editingCustomer.record_status || "active",
      successor_customer_id: editingCustomer.successor_customer_id || "",
      closed_note: editingCustomer.closed_note || "",
      tags: editingCustomer.tags
        ? editingCustomer.tags.map((t) => ({
            label: t.name,
            value: t.id,
            color: t.color,
          }))
        : [],
      note: editingCustomer.note || "",
    });
    setRepresentatives(editingCustomer.representatives || []);
    setEditingRepIndex(null);
  }, [isOpen, editingCustomer]);

  useEffect(() => {
    if (!isOpen || !formData.branch_id) {
      setSuccessorOptions([]);
      return;
    }
    let cancelled = false;
    getSimpleCustomers({ branch_id: formData.branch_id })
      .then((res) => {
        if (cancelled) return;
        const selfId = editingCustomer?.id;
        const items = Array.isArray(res?.items) ? res.items : [];
        const opts = items
          .filter((c) => c.id && c.id !== selfId)
          .map((c) => ({
            value: c.id,
            label: c.ward ? `${c.name} — ${c.ward}` : c.name,
          }));
        if (
          editingCustomer?.successor_customer_id &&
          !opts.some((o) => o.value === editingCustomer.successor_customer_id)
        ) {
          opts.unshift({
            value: editingCustomer.successor_customer_id,
            label:
              editingCustomer.successor_customer_name ||
              "Hồ sơ kế thừa (đã chọn)",
          });
        }
        setSuccessorOptions(opts);
      })
      .catch(() => {
        if (!cancelled) setSuccessorOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, formData.branch_id, editingCustomer?.id]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "name"
          ? value.toUpperCase()
          : name === "student_count"
            ? value.replace(/\D/g, "")
            : value,
    }));
  };

  const handleSubmit = async () => {
    if (isInvalid) {
      if (formData.record_status === "merged" && !formData.successor_customer_id) {
        showWarningToast("Khi chọn Đã sáp nhập, vui lòng chọn trường/khách hàng kế thừa.");
        return;
      }
      showWarningToast(
        requiresLeadSource
          ? "Vui lòng nhập đủ tên khách hàng, chi nhánh, loại khách hàng, nguồn khách và cấp học (với Trường học)"
          : "Vui lòng nhập đủ tên khách hàng, chi nhánh, loại khách hàng và cấp học (với Trường học)"
      );
      return;
    }

    const sessionBranchId = readUserBranchId();
    const effectiveBranchId = canPickBranch ? formData.branch_id : sessionBranchId;
    if (!effectiveBranchId) {
      showWarningToast(
        canPickBranch
          ? "Vui lòng chọn chi nhánh."
          : "Tài khoản chưa gắn chi nhánh — không thể tạo khách hàng."
      );
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        ward: formData.ward?.trim() || null,
        student_count: formData.student_count?.trim()
          ? parseInt(formData.student_count, 10)
          : null,
        branch_id: effectiveBranchId,
        owner_id: formData.owner_id,
        lead_source: formData.lead_source,
        customer_type: formData.customer_type,
        school_level: formData.customer_type === "Trường học" ? formData.school_level : null,
        customer_status: formData.customer_status,
        record_status: formData.record_status || "active",
        successor_customer_id:
          formData.record_status === "merged"
            ? formData.successor_customer_id || null
            : null,
        closed_note:
          formData.record_status === "closed" || formData.record_status === "merged"
            ? formData.closed_note?.trim() || null
            : null,
        tag_ids: formData.tags?.map((t) => t.value) || [],
        note: formData.note || "",
        representatives,
      };

      if (editingCustomer) {
        await updateCustomer(editingCustomer.id, payload);
      } else {
        await createCustomer(payload);
      }

      showSuccessToast(
        editingCustomer ? "Cập nhật khách hàng thành công!" : "Tạo khách hàng thành công!"
      );

      onSuccess?.();
      resetForm();
      onClose();
    } catch (err) {
      console.error(err);
      showErrorToast("Không thể lưu khách hàng");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-[210] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              className="pointer-events-auto flex h-[92dvh] w-full max-w-5xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-5 py-4 sm:px-6"
                data-tour="customer-create-modal-header"
              >
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm">
                    <Users className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-black uppercase tracking-tight text-gray-800 sm:text-xl">
                      {editingCustomer ? "Cập nhật" : "Thêm"}{" "}
                      <span className="text-red-700">khách hàng</span>
                    </h2>
                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      Hồ sơ liên hệ, CRM và người đại diện
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={handleQuickTour}
                    className="hidden items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-red-700 transition-colors hover:bg-red-100 sm:inline-flex"
                  >
                    <Sparkles className="h-4 w-4" />
                    Hướng dẫn
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                    aria-label="Đóng"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-slate-50/50">
              <div className="grid grid-cols-1 gap-5 p-5 sm:gap-6 sm:p-6 md:grid-cols-2">
                <div className="space-y-6">
                  <div data-tour="customer-create-modal-identity">
                  <Section icon={Building2} title="Khách hàng" subTitle="Thông tin nhận diện chính">
                    <div className="space-y-4">
                      <div className="relative">
                        <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                          Tên khách hàng *
                        </label>
                        <input
                          name="name"
                          value={formData.name}
                          onChange={handleInput}
                          placeholder="CÔNG TY / TRƯỜNG HỌC / ĐỐI TÁC"
                          className="w-full rounded-xl border border-gray-200 px-4 py-3 font-bold text-gray-800 outline-none transition-all focus:border-red-400 focus:ring-2 focus:ring-red-100"
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="relative">
                          <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                            Số điện thoại
                          </label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                              name="phone"
                              value={formData.phone}
                              onChange={handleInput}
                              placeholder="0xxx xxx xxx"
                              className="w-full rounded-xl border border-gray-200 py-2.5 pl-9 pr-4 font-medium outline-none transition-all focus:border-red-400 focus:ring-2 focus:ring-red-100"
                            />
                          </div>
                        </div>

                        <div className="relative">
                          <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                            Loại khách hàng *
                          </label>
                          <Select
                            options={customerTypeOptions}
                            value={customerTypeOptions.find((x) => x.value === formData.customer_type) || null}
                            onChange={(s) =>
                              setFormData((prev) => ({
                                ...prev,
                                customer_type: s?.value || "",
                              }))
                            }
                            styles={selectStyles}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                          />
                        </div>
                      </div>

                      {formData.customer_type === "Trường học" ? (
                        <div className="relative">
                          <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                            Cấp học *
                          </label>
                          <Select
                            options={schoolLevelOptions}
                            value={schoolLevelOptions.find((x) => x.value === formData.school_level) || null}
                            onChange={(s) =>
                              setFormData((prev) => ({
                                ...prev,
                                school_level: s?.value || "",
                              }))
                            }
                            styles={selectStyles}
                            placeholder="-- Chọn cấp học --"
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                          />
                        </div>
                      ) : null}

                      <div className="relative">
                        <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                          Địa chỉ
                        </label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <textarea
                            name="address"
                            value={formData.address || ""}
                            onChange={handleInput}
                            rows={3}
                            placeholder="Số nhà, đường, tỉnh/thành..."
                            className="min-h-[88px] w-full resize-y rounded-xl border border-gray-200 py-2.5 pl-9 pr-4 font-medium outline-none transition-all focus:border-red-400 focus:ring-2 focus:ring-red-100"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="relative">
                          <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                            Phường / Xã
                          </label>
                          <input
                            name="ward"
                            value={formData.ward}
                            onChange={handleInput}
                            placeholder="Ví dụ: Phường Phú Thọ"
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 font-medium text-gray-800 outline-none transition-all focus:border-red-400 focus:ring-2 focus:ring-red-100"
                          />
                        </div>

                        <div className="relative">
                          <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                            Sĩ số học sinh
                          </label>
                          <div className="relative">
                            <Users className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                              name="student_count"
                              value={formData.student_count}
                              onChange={handleInput}
                              inputMode="numeric"
                              placeholder="Ví dụ: 450"
                              className="w-full rounded-xl border border-gray-200 py-3 pl-9 pr-4 font-medium text-gray-800 outline-none transition-all focus:border-red-400 focus:ring-2 focus:ring-red-100"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Section>
                  </div>

                  <div data-tour="customer-create-modal-ops">
                  <Section icon={UserRound} title="Điều phối CRM" subTitle="Chi nhánh và người phụ trách">
                    <div className="space-y-4">
                      <div className="relative">
                        <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                          Chi nhánh *
                        </label>
                        <Select
                          options={branchOptions}
                          value={branchOptions.find((x) => x.value === formData.branch_id) || null}
                          onChange={(s) =>
                            setFormData((prev) => ({
                              ...prev,
                              branch_id: s?.value || "",
                            }))
                          }
                          styles={selectStyles}
                          placeholder={
                            canPickBranch ? "-- Chọn chi nhánh --" : "Chi nhánh của bạn"
                          }
                          isDisabled={!canPickBranch}
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                        />
                        {!canPickBranch ? (
                          <p className="mt-1 text-[10px] font-medium text-gray-500">
                            Chi nhánh theo tài khoản đăng nhập — chỉ tạo khách hàng thuộc chi nhánh của bạn.
                          </p>
                        ) : null}
                      </div>

                      <div className="relative">
                        <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                          Sales phụ trách
                        </label>
                        <Select
                          options={salesOptions}
                          value={salesOptions.find((x) => x.value === formData.owner_id) || null}
                          onChange={(s) =>
                            setFormData((prev) => ({
                              ...prev,
                              owner_id: s?.value || "",
                            }))
                          }
                          styles={selectStyles}
                          placeholder="-- Chọn Sales --"
                          isClearable
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                        />
                      </div>
                    </div>
                  </Section>
                  </div>
                </div>

                <div className="space-y-6">
                  <div data-tour="customer-create-modal-crm">
                  <Section icon={Briefcase} title="Thông tin CRM" subTitle="Chăm sóc CRM và tình trạng hoạt động (sáp nhập/đóng)">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="relative">
                          <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                            Nguồn khách *
                          </label>
                          <Select
                            options={leadSourceOptions}
                            value={
                              formData.lead_source
                                ? {
                                    value: formData.lead_source,
                                    label: leadSourceLabels[formData.lead_source],
                                  }
                                : null
                            }
                            onChange={(s) =>
                              setFormData((prev) => ({
                                ...prev,
                                lead_source: s?.value || "",
                              }))
                            }
                            styles={selectStyles}
                            placeholder="-- Chọn nguồn khách --"
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                          />
                        </div>

                        <div className="relative">
                          <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                            Trạng thái chăm sóc
                          </label>
                          <Select
                            options={customerStatusOptions}
                            value={customerStatusOptions.find((x) => x.value === formData.customer_status) || null}
                            onChange={(s) =>
                              setFormData((prev) => ({
                                ...prev,
                                customer_status: s?.value || "new",
                              }))
                            }
                            styles={selectStyles}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="relative">
                          <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                            Tình trạng hoạt động
                          </label>
                          <Select
                            options={recordStatusOptions}
                            value={
                              recordStatusOptions.find(
                                (x) => x.value === formData.record_status
                              ) || recordStatusOptions[0]
                            }
                            onChange={(s) =>
                              setFormData((prev) => ({
                                ...prev,
                                record_status: s?.value || "active",
                                successor_customer_id:
                                  s?.value === "merged"
                                    ? prev.successor_customer_id
                                    : "",
                                closed_note:
                                  s?.value === "active" ? "" : prev.closed_note,
                              }))
                            }
                            styles={selectStyles}
                            menuPortalTarget={
                              typeof document !== "undefined" ? document.body : null
                            }
                            menuPosition="fixed"
                          />
                        </div>

                        <div className="relative">
                          <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                            Tags
                          </label>
                          <Select
                            isMulti
                            options={tagOptions}
                            value={formData.tags}
                            onChange={(tags) =>
                              setFormData((prev) => ({ ...prev, tags: tags || [] }))
                            }
                            styles={selectStyles}
                            placeholder="-- Chọn tags --"
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                          />
                        </div>
                      </div>

                      {formData.record_status === "merged" ? (
                        <div className="relative">
                          <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                            Trường / KH kế thừa *
                          </label>
                          <Select
                            options={successorOptions}
                            value={
                              successorOptions.find(
                                (x) => x.value === formData.successor_customer_id
                              ) || null
                            }
                            onChange={(s) =>
                              setFormData((prev) => ({
                                ...prev,
                                successor_customer_id: s?.value || "",
                              }))
                            }
                            styles={selectStyles}
                            placeholder="Chọn hồ sơ đang hoạt động..."
                            isClearable
                            menuPortalTarget={
                              typeof document !== "undefined" ? document.body : null
                            }
                            menuPosition="fixed"
                          />
                        </div>
                      ) : null}

                      {formData.record_status === "closed" ||
                      formData.record_status === "merged" ? (
                        <div className="relative">
                          <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                            Ghi chú đóng / sáp nhập
                          </label>
                          <input
                            type="text"
                            name="closed_note"
                            value={formData.closed_note}
                            onChange={handleInput}
                            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium outline-none transition-all focus:border-red-400 focus:ring-2 focus:ring-red-100"
                            placeholder="VD: Sáp nhập năm học 2025-2026 theo QĐ..."
                          />
                        </div>
                      ) : null}

                      <div className="relative">
                        <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase text-gray-400">
                          Ghi chú
                        </label>
                        <div className="relative">
                          <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <textarea
                            name="note"
                            value={formData.note}
                            onChange={handleInput}
                            rows={4}
                            className="w-full rounded-xl border border-gray-200 py-2.5 pl-9 pr-4 font-medium outline-none transition-all focus:border-red-400 focus:ring-2 focus:ring-red-100"
                            placeholder="Mô tả thêm về nhu cầu, lịch sử làm việc hoặc lưu ý nội bộ..."
                          />
                        </div>
                      </div>
                    </div>
                  </Section>
                  </div>

                  <div data-tour="customer-create-modal-contacts">
                  <Section icon={Users} title="Người liên hệ" subTitle="Đầu mối làm việc của khách hàng">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium text-gray-400">
                          Quản lý danh sách đại diện liên hệ và thông tin ngân hàng.
                        </p>
                        <button
                          onClick={() => {
                            setEditingRepIndex(null);
                            setRepModalOpen(true);
                          }}
                          className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-red-700"
                        >
                          <Plus size={14} /> Thêm
                        </button>
                      </div>

                      {representatives.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-5 text-sm italic text-gray-500">
                          Chưa có người liên hệ
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {representatives.map((rep, i) => (
                            <div
                              key={rep.id || `${rep.name || "rep"}-${i}`}
                              className="flex items-start justify-between gap-3 rounded-2xl border border-gray-100 bg-gray-50/60 p-4"
                            >
                              <div className="min-w-0">
                                <div className="text-sm font-bold text-gray-800">{rep.name}</div>
                                <div className="mt-1 text-sm text-gray-600">{rep.phone || "—"}</div>
                                <div className="text-xs text-gray-500">{rep.position || "Chưa có chức vụ"}</div>
                              </div>

                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    setEditingRepIndex(i);
                                    setRepModalOpen(true);
                                  }}
                                  className="rounded-lg p-2 text-gray-500 transition hover:bg-white hover:text-red-600"
                                >
                                  <Pencil size={16} />
                                </button>
                                <button
                                  onClick={() =>
                                    setRepresentatives((prev) => prev.filter((_, idx) => idx !== i))
                                  }
                                  className="rounded-lg p-2 text-gray-500 transition hover:bg-red-50 hover:text-red-600"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Section>
                  </div>

                  <div
                    className="rounded-2xl border border-blue-100 bg-blue-50 p-5"
                    data-tour="customer-create-modal-hint"
                  >
                    <div className="flex gap-3">
                      <ShieldCheck className="shrink-0 text-blue-500" size={20} />
                      <p className="text-xs font-medium leading-relaxed text-blue-700">
                        Hồ sơ khách hàng sẽ được dùng xuyên suốt trong CRM để theo dõi chăm sóc,
                        hợp đồng và lịch sử tương tác. Hãy đảm bảo chi nhánh, nguồn khách và đầu mối
                        liên hệ được khai báo chính xác.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              </div>

              <div
                className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-5 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-6"
                data-tour="customer-create-modal-footer"
              >
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-gray-200 bg-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading || isInvalid}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-6 py-2.5 text-xs font-black uppercase tracking-wider text-white shadow-md shadow-red-100 transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />}
                  {loading ? "Đang xử lý..." : "Lưu hồ sơ"}
                </button>
              </div>
            </motion.div>
          </div>

          <ContactRepresentativeModal
            open={repModalOpen}
            onClose={() => {
              setRepModalOpen(false);
              setEditingRepIndex(null);
            }}
            defaultValue={editingRepIndex !== null ? representatives[editingRepIndex] : null}
            onSave={(data) => {
              if (editingRepIndex === null) {
                setRepresentatives((prev) => [...prev, data]);
              } else {
                setRepresentatives((prev) =>
                  prev.map((item, idx) => (idx === editingRepIndex ? data : item))
                );
              }
            }}
          />
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
